import React, { useCallback, useState } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, FlatList, ActivityIndicator,
  TextInput, Modal, ScrollView, Switch, Platform,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  getScheduledTasks,
  createScheduledTask,
  updateScheduledTask,
  deleteScheduledTask,
  toggleScheduledTask,
} from '../../services/schedulerService';
import {
  ScheduledTask,
  ScheduledTaskPeriod,
  ScheduledTaskSchedule,
  ReportType,
} from '../../types';
import CrossPlatformAlert from '../../components/CrossPlatformAlert';

const REPORT_TYPES: ReportType[] = ['current_quantity', 'usage_records', 'real_usage', 'below_limit'];
const SCHEDULES: ScheduledTaskSchedule[] = ['daily', 'weekly', 'monthly'];
const PERIODS: ScheduledTaskPeriod[] = [
  'none', 'last_7_days', 'last_week', 'last_month', 'last_year', 'current_week', 'current_month',
];

interface TaskForm {
  reportType: ReportType;
  schedule: ScheduledTaskSchedule;
  period: ScheduledTaskPeriod;
  recipients: string[];
  enabled: boolean;
  dayOfWeek: number;
  dayOfMonth: number;
}

const DEFAULT_FORM: TaskForm = {
  reportType: 'current_quantity',
  schedule: 'daily',
  period: 'none',
  recipients: [],
  enabled: true,
  dayOfWeek: 1,
  dayOfMonth: 1,
};

export default function SchedulerScreen() {
  const { appUser } = useAuth();
  const { t } = useLanguage();

  const [tasks, setTasks] = useState<ScheduledTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState<ScheduledTask | null>(null);
  const [form, setForm] = useState<TaskForm>(DEFAULT_FORM);
  const [recipientInput, setRecipientInput] = useState('');
  const [saving, setSaving] = useState(false);

  const isAdmin = appUser?.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getScheduledTasks();
      setTasks(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { load(); }, [load]));

  const openAdd = () => {
    setEditingTask(null);
    setForm(DEFAULT_FORM);
    setRecipientInput('');
    setModalVisible(true);
  };

  const openEdit = (task: ScheduledTask) => {
    setEditingTask(task);
    setForm({
      reportType: task.reportType,
      schedule: task.schedule,
      period: task.period,
      recipients: [...task.recipients],
      enabled: task.enabled,
      dayOfWeek: task.dayOfWeek ?? 1,
      dayOfMonth: task.dayOfMonth ?? 1,
    });
    setRecipientInput('');
    setModalVisible(true);
  };

  const handleAddRecipient = () => {
    const email = recipientInput.trim();
    if (!email) return;
    if (!form.recipients.includes(email)) {
      setForm(f => ({ ...f, recipients: [...f.recipients, email] }));
    }
    setRecipientInput('');
  };

  const handleRemoveRecipient = (email: string) => {
    setForm(f => ({ ...f, recipients: f.recipients.filter(r => r !== email) }));
  };

  const handleSave = async () => {
    if (form.recipients.length === 0) {
      CrossPlatformAlert.alert(t('common.validation'), t('scheduler.validationRecipients'));
      return;
    }
    setSaving(true);
    try {
      if (editingTask) {
        await updateScheduledTask(editingTask.id, {
          reportType: form.reportType,
          schedule: form.schedule,
          period: form.period,
          recipients: form.recipients,
          enabled: form.enabled,
          dayOfWeek: form.schedule === 'weekly' ? form.dayOfWeek : undefined,
          dayOfMonth: form.schedule === 'monthly' ? form.dayOfMonth : undefined,
        });
      } else {
        await createScheduledTask({
          type: 'report',
          reportType: form.reportType,
          schedule: form.schedule,
          period: form.period,
          recipients: form.recipients,
          enabled: form.enabled,
          dayOfWeek: form.schedule === 'weekly' ? form.dayOfWeek : undefined,
          dayOfMonth: form.schedule === 'monthly' ? form.dayOfMonth : undefined,
          createdBy: appUser?.uid ?? '',
        });
      }
      setModalVisible(false);
      load();
    } catch (err: any) {
      CrossPlatformAlert.alert(t('common.error'), err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (task: ScheduledTask) => {
    CrossPlatformAlert.alert(
      t('scheduler.deleteTask'),
      t('scheduler.deleteConfirm'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.del'), style: 'destructive', onPress: async () => {
            try {
              await deleteScheduledTask(task.id);
              load();
            } catch (err: any) {
              CrossPlatformAlert.alert(t('common.error'), err.message);
            }
          },
        },
      ]
    );
  };

  const handleToggle = async (task: ScheduledTask) => {
    try {
      await toggleScheduledTask(task.id, !task.enabled);
      load();
    } catch (err: any) {
      CrossPlatformAlert.alert(t('common.error'), err.message);
    }
  };

  const reportTypeLabel = (rt: ReportType): string => {
    if (rt === 'current_quantity') return t('reports.currentQuantityTitle');
    if (rt === 'real_usage') return t('reports.realUsageTitle');
    if (rt === 'below_limit') return t('reports.belowLimitTitle');
    return t('reports.usageRecordsTitle');
  };

  const weekDayLabel = (dow: number): string => {
    const keys = [
      t('scheduler.weekDaySun'),
      t('scheduler.weekDayMon'),
      t('scheduler.weekDayTue'),
      t('scheduler.weekDayWed'),
      t('scheduler.weekDayThu'),
      t('scheduler.weekDayFri'),
      t('scheduler.weekDaySat'),
    ];
    return keys[dow] ?? String(dow);
  };

  const scheduleLabel = (s: ScheduledTaskSchedule, dow?: number, dom?: number): string => {
    if (s === 'daily') return t('scheduler.scheduleDaily');
    if (s === 'weekly') return `${t('scheduler.scheduleWeekly')} (${weekDayLabel(dow ?? 1)})`;
    return `${t('scheduler.scheduleMonthly')} (${dom ?? 1}.)`;
  };

  const periodLabel = (p: ScheduledTaskPeriod): string => {
    if (p === 'last_7_days') return t('scheduler.periodLast7Days');
    if (p === 'last_week') return t('scheduler.periodLastWeek');
    if (p === 'last_month') return t('scheduler.periodLastMonth');
    if (p === 'last_year') return t('scheduler.periodLastYear');
    if (p === 'current_week') return t('scheduler.periodCurrentWeek');
    if (p === 'current_month') return t('scheduler.periodCurrentMonth');
    return t('scheduler.periodNone');
  };

  if (!isAdmin) {
    return <View style={styles.center}><Text style={styles.noAccess}>{t('scheduler.noAccess')}</Text></View>;
  }

  const renderPickerRow = <T extends string>(
    label: string,
    options: T[],
    selected: T,
    onSelect: (v: T) => void,
    labelFn: (v: T) => string,
  ) => (
    <View style={styles.formGroup}>
      <Text style={styles.formLabel}>{label}</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.pickerRow}>
        {options.map(opt => (
          <TouchableOpacity
            key={opt}
            style={[styles.pickerBtn, selected === opt && styles.pickerBtnActive]}
            onPress={() => onSelect(opt)}
          >
            <Text style={[styles.pickerBtnText, selected === opt && styles.pickerBtnTextActive]}>
              {labelFn(opt)}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={styles.innerContent}>
        <View style={styles.header}>
          <Text style={styles.title}>{t('scheduler.title')}</Text>
          <TouchableOpacity style={styles.addBtn} onPress={openAdd}>
            <Text style={styles.addBtnText}>{t('scheduler.addTask')}</Text>
          </TouchableOpacity>
        </View>

        {loading ? (
          <ActivityIndicator style={{ marginTop: 40 }} size="large" color="#2563EB" />
        ) : (
          <FlatList
            data={tasks}
            keyExtractor={item => item.id}
            contentContainerStyle={styles.listContent}
            renderItem={({ item }) => (
              <View style={styles.card}>
                <View style={styles.cardTop}>
                  <View style={styles.cardMain}>
                    <View style={styles.cardTitleRow}>
                      <Text style={styles.cardTitle}>{reportTypeLabel(item.reportType)}</Text>
                      <View style={[styles.statusBadge, { backgroundColor: item.enabled ? '#10B981' : '#94A3B8' }]}>
                        <Text style={styles.statusText}>
                          {item.enabled ? t('scheduler.enabledStatus') : t('scheduler.disabledStatus')}
                        </Text>
                      </View>
                    </View>
                    <Text style={styles.cardMeta}>
                      {scheduleLabel(item.schedule, item.dayOfWeek, item.dayOfMonth)} · {periodLabel(item.period)}
                    </Text>
                    <Text style={styles.cardRecipients} numberOfLines={2}>
                      {item.recipients.join(', ')}
                    </Text>
                  </View>
                  <Switch
                    value={item.enabled}
                    onValueChange={() => handleToggle(item)}
                    trackColor={{ false: '#E2E8F0', true: '#86EFAC' }}
                    thumbColor={item.enabled ? '#10B981' : '#CBD5E1'}
                  />
                </View>
                <View style={styles.cardActions}>
                  <TouchableOpacity style={styles.editBtn} onPress={() => openEdit(item)}>
                    <Text style={styles.editBtnText}>{t('common.edit')}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.deleteBtn} onPress={() => handleDelete(item)}>
                    <Text style={styles.deleteBtnText}>{t('scheduler.deleteTask')}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
            ListEmptyComponent={<Text style={styles.empty}>{t('scheduler.noTasks')}</Text>}
          />
        )}
      </View>

      <Modal
        visible={modalVisible}
        animationType="slide"
        transparent
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <Text style={styles.modalTitle}>
                {editingTask ? t('scheduler.editTask') : t('scheduler.addTask')}
              </Text>

              {renderPickerRow(
                t('scheduler.reportTypeLabel'),
                REPORT_TYPES,
                form.reportType,
                rt => setForm(f => ({ ...f, reportType: rt })),
                reportTypeLabel,
              )}

              {renderPickerRow(
                t('scheduler.scheduleLabel'),
                SCHEDULES,
                form.schedule,
                s => setForm(f => ({ ...f, schedule: s })),
                scheduleLabel,
              )}

              {form.schedule === 'weekly' && (() => {
                const WEEK_DAYS: number[] = [1, 2, 3, 4, 5, 6, 0];
                return (
                  <View style={styles.formGroup}>
                    <Text style={styles.formLabel}>{t('scheduler.dayOfWeekLabel')}</Text>
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.pickerRow}>
                      {WEEK_DAYS.map(dow => (
                        <TouchableOpacity
                          key={dow}
                          style={[styles.pickerBtn, form.dayOfWeek === dow && styles.pickerBtnActive]}
                          onPress={() => setForm(f => ({ ...f, dayOfWeek: dow }))}
                        >
                          <Text style={[styles.pickerBtnText, form.dayOfWeek === dow && styles.pickerBtnTextActive]}>
                            {weekDayLabel(dow)}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  </View>
                );
              })()}

              {form.schedule === 'monthly' && (() => {
                const DAYS_OF_MONTH: number[] = Array.from({ length: 31 }, (_, i) => i + 1);
                return (
                  <View style={styles.formGroup}>
                    <Text style={styles.formLabel}>{t('scheduler.dayOfMonthLabel')}</Text>
                    <View style={styles.pickerRow}>
                      {DAYS_OF_MONTH.map(dom => (
                        <TouchableOpacity
                          key={dom}
                          style={[styles.pickerBtn, form.dayOfMonth === dom && styles.pickerBtnActive]}
                          onPress={() => setForm(f => ({ ...f, dayOfMonth: dom }))}
                        >
                          <Text style={[styles.pickerBtnText, form.dayOfMonth === dom && styles.pickerBtnTextActive]}>
                            {String(dom)}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                );
              })()}

              {renderPickerRow(
                t('scheduler.periodLabel'),
                PERIODS,
                form.period,
                p => setForm(f => ({ ...f, period: p })),
                periodLabel,
              )}

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>{t('scheduler.recipientsLabel')}</Text>
                <View style={styles.recipientInputRow}>
                  <TextInput
                    style={styles.recipientInput}
                    value={recipientInput}
                    onChangeText={setRecipientInput}
                    placeholder={t('scheduler.recipientPlaceholder')}
                    autoCapitalize="none"
                    keyboardType="email-address"
                    onSubmitEditing={handleAddRecipient}
                    returnKeyType="done"
                  />
                  <TouchableOpacity style={styles.recipientAddBtn} onPress={handleAddRecipient}>
                    <Text style={styles.recipientAddBtnText}>+</Text>
                  </TouchableOpacity>
                </View>
                {form.recipients.map(email => (
                  <View key={email} style={styles.recipientChip}>
                    <Text style={styles.recipientChipText} numberOfLines={1}>{email}</Text>
                    <TouchableOpacity onPress={() => handleRemoveRecipient(email)}>
                      <Text style={styles.recipientChipRemove}>✕</Text>
                    </TouchableOpacity>
                  </View>
                ))}
              </View>

              <View style={styles.formGroup}>
                <View style={styles.enabledRow}>
                  <Text style={styles.formLabel}>{t('scheduler.enabledLabel')}</Text>
                  <Switch
                    value={form.enabled}
                    onValueChange={v => setForm(f => ({ ...f, enabled: v }))}
                    trackColor={{ false: '#E2E8F0', true: '#86EFAC' }}
                    thumbColor={form.enabled ? '#10B981' : '#CBD5E1'}
                  />
                </View>
              </View>

              <View style={styles.modalActions}>
                <TouchableOpacity
                  style={styles.cancelBtn}
                  onPress={() => setModalVisible(false)}
                  disabled={saving}
                >
                  <Text style={styles.cancelBtnText}>{t('common.cancel')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.saveBtn, saving && styles.saveBtnDisabled]}
                  onPress={handleSave}
                  disabled={saving}
                >
                  {saving
                    ? <ActivityIndicator size="small" color="#FFF" />
                    : <Text style={styles.saveBtnText}>{t('scheduler.saveTask')}</Text>}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  innerContent: { flex: 1, maxWidth: 960, width: '100%', alignSelf: 'center' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  noAccess: { fontSize: 18, color: '#6B7280' },
  header: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    padding: 16, borderBottomWidth: 1, borderBottomColor: '#E2E8F0',
  },
  title: { fontSize: 22, fontWeight: 'bold', color: '#1E293B' },
  addBtn: { backgroundColor: '#2563EB', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8 },
  addBtnText: { color: '#FFF', fontWeight: '600', fontSize: 14 },
  listContent: { padding: 12 },
  card: {
    backgroundColor: '#FFF', borderRadius: 12, padding: 14, marginBottom: 10,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 1,
  },
  cardTop: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 10 },
  cardMain: { flex: 1, marginRight: 10 },
  cardTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 },
  cardTitle: { fontSize: 15, fontWeight: '700', color: '#1E293B' },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 },
  statusText: { color: '#FFF', fontSize: 11, fontWeight: '700' },
  cardMeta: { fontSize: 12, color: '#64748B', marginBottom: 4 },
  cardRecipients: { fontSize: 12, color: '#94A3B8' },
  cardActions: { flexDirection: 'row', gap: 8 },
  editBtn: {
    paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6,
    borderWidth: 1, borderColor: '#2563EB', backgroundColor: '#EFF6FF',
  },
  editBtnText: { fontSize: 12, fontWeight: '600', color: '#2563EB' },
  deleteBtn: {
    paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6,
    borderWidth: 1, borderColor: '#EF4444', backgroundColor: '#FEF2F2',
  },
  deleteBtnText: { fontSize: 12, fontWeight: '600', color: '#EF4444' },
  empty: { textAlign: 'center', color: '#94A3B8', marginTop: 40, fontSize: 15 },

  // Modal
  modalOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    maxHeight: '90%',
    ...Platform.select({ web: { maxWidth: 600, alignSelf: 'center', width: '100%', borderRadius: 16, marginBottom: 20 } }),
  },
  modalTitle: { fontSize: 18, fontWeight: '700', color: '#1E293B', marginBottom: 16 },
  formGroup: { marginBottom: 16 },
  formLabel: { fontSize: 13, fontWeight: '600', color: '#475569', marginBottom: 8 },
  pickerRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  pickerBtn: {
    paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8,
    borderWidth: 1, borderColor: '#E2E8F0', backgroundColor: '#F8FAFC',
  },
  pickerBtnActive: { backgroundColor: '#2563EB', borderColor: '#2563EB' },
  pickerBtnText: { fontSize: 12, fontWeight: '600', color: '#475569' },
  pickerBtnTextActive: { color: '#FFF' },
  recipientInputRow: { flexDirection: 'row', gap: 8 },
  recipientInput: {
    flex: 1, borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8,
    paddingHorizontal: 12, paddingVertical: 9, fontSize: 14, backgroundColor: '#F8FAFC',
  },
  recipientAddBtn: {
    width: 44, height: 44, borderRadius: 8, backgroundColor: '#2563EB',
    justifyContent: 'center', alignItems: 'center',
  },
  recipientAddBtnText: { color: '#FFF', fontSize: 22, fontWeight: '700', lineHeight: 26 },
  recipientChip: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: '#EFF6FF', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 7,
    marginTop: 6,
  },
  recipientChipText: { fontSize: 13, color: '#1E40AF', flex: 1 },
  recipientChipRemove: { fontSize: 14, color: '#3B82F6', marginLeft: 8, fontWeight: '700' },
  enabledRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  modalActions: { flexDirection: 'row', gap: 10, marginTop: 8, marginBottom: 8 },
  cancelBtn: {
    flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1,
    borderColor: '#E2E8F0', alignItems: 'center',
  },
  cancelBtnText: { fontSize: 14, fontWeight: '600', color: '#475569' },
  saveBtn: {
    flex: 2, paddingVertical: 12, borderRadius: 8, backgroundColor: '#2563EB', alignItems: 'center',
  },
  saveBtnDisabled: { backgroundColor: '#93C5FD' },
  saveBtnText: { fontSize: 14, fontWeight: '700', color: '#FFF' },
});
